import { createReducer } from '@reduxjs/toolkit';

import LoadingStatus from '@libs/loading-status';

import { Label } from '@app/features/label/types';
import { Participant } from '@app/store/transport-layer/template/contract-links';

import {
  checkContractLinkLimits,
  confirmThatFieldCannotBeModified,
  contractLinkCreateFileUpload,
  contractLinkCreateLoadPdfSuccess,
  contractLinkCreateMaxSigningPerParticipantChange,
  contractLinkCreateSetLabels,
} from './actions';

export interface ContractLinkCreateUpload {
  extension: string | null;
  fileId: string | null;
  fileName: string | null;
  loadingStatus: LoadingStatus;
  url: string | null;
}

export interface ContractLinkCreateState {
  confirmedThatFieldCannotBeModified: boolean;
  labels: Label[];
  linkPassword: {
    value: string;
    hint: string | null;
  } | null;
  maxSigningPerParticipant: number | null;
  maxSigningPerParticipantLimit: number | null;
  participants: Participant[];
  upload: ContractLinkCreateUpload;
}

const initialState: ContractLinkCreateState = {
  confirmedThatFieldCannotBeModified: false,
  labels: [],
  linkPassword: null,
  maxSigningPerParticipant: null,
  maxSigningPerParticipantLimit: null,
  participants: [],
  upload: {
    extension: null,
    fileId: null,
    fileName: null,
    loadingStatus: LoadingStatus.IDLE,
    url: null,
  },
};

export const reducer = createReducer(initialState, (builder) => {
  builder
    .addCase(checkContractLinkLimits.pending, () => initialState)
    .addCase(checkContractLinkLimits.fulfilled, (state, action) => {
      state.maxSigningPerParticipantLimit = action.payload.maxSigningPerSignerLimit;
    })
    .addCase(contractLinkCreateFileUpload.pending, (state, action) => {
      const { arg } = action.meta;
      state.upload.extension = arg.name?.split('.').pop()?.toLowerCase() ?? '';
      state.upload.loadingStatus = LoadingStatus.PENDING;
    })
    .addCase(contractLinkCreateFileUpload.fulfilled, (state, action) => {
      if (action.payload) {
        const { fileId, title, url } = action.payload;
        state.upload.fileId = fileId;
        state.upload.fileName = title;
        state.upload.url = url;
      }
      state.upload.loadingStatus = LoadingStatus.SUCCEEDED;
    })
    .addCase(contractLinkCreateFileUpload.rejected, (state) => {
      state.upload.loadingStatus = LoadingStatus.FAILED;
    })
    .addCase(contractLinkCreateMaxSigningPerParticipantChange, (state, action) => {
      state.maxSigningPerParticipant = action.payload.maxSigningPerParticipant;
    })
    .addCase(contractLinkCreateLoadPdfSuccess, (state, action) => {
      const { fileId, title, url } = action.payload;
      state.upload.fileId = fileId;
      state.upload.fileName = title;
      state.upload.url = url;
    })
    .addCase(contractLinkCreateSetLabels, (state, action) => {
      state.labels = action.payload;
    })
    .addCase(confirmThatFieldCannotBeModified, (state) => {
      state.confirmedThatFieldCannotBeModified = true;
    });
});
